import { useCallback, useEffect, useRef, useState } from 'react'

interface DraftEnvelope<T> {
  version: 1
  savedAt: string
  value: T
}

function readSavedAt(storageKey: string): string | null {
  try {
    const raw = window.localStorage.getItem(storageKey)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<DraftEnvelope<unknown>>
    return parsed?.version === 1 && typeof parsed.savedAt === 'string' ? parsed.savedAt : null
  } catch {
    return null
  }
}

/**
 * Debounced local draft persistence for long forms.
 *
 * The draft is deliberately browser-local: it protects typed work from navigation, refreshes
 * and transient API failures without pretending to be a server-side shared draft. Callers
 * decide how to merge restored values with current defaults.
 */
export function useDraftAutosave<T>({
  storageKey,
  enabled,
  dirty,
  value,
  onRestore,
  debounceMs = 650,
}: {
  storageKey: string
  enabled: boolean
  dirty: boolean
  value: T
  onRestore: (value: T) => void
  debounceMs?: number
}) {
  const [savedState, setSavedState] = useState<{ key: string; savedAt: string | null }>(() => ({
    key: storageKey,
    savedAt: readSavedAt(storageKey),
  }))
  const restoredKey = useRef<string | null>(null)
  const restoreRef = useRef(onRestore)

  // Keep the latest callback without mutating refs during render. This effect is declared before
  // restoration so a newly supplied callback is visible to the restore effect in the same commit.
  useEffect(() => {
    restoreRef.current = onRestore
  }, [onRestore])

  useEffect(() => {
    if (!enabled) {
      restoredKey.current = null
      return
    }
    if (restoredKey.current === storageKey) return
    restoredKey.current = storageKey
    try {
      const raw = window.localStorage.getItem(storageKey)
      if (!raw) return
      const parsed = JSON.parse(raw) as DraftEnvelope<T>
      if (parsed?.version !== 1 || !parsed.value) return
      restoreRef.current(parsed.value)
    } catch {
      // A malformed/blocked localStorage entry must never stop the form from opening.
    }
  }, [enabled, storageKey])

  const saveNow = useCallback(() => {
    if (!enabled || !dirty) return
    try {
      const savedAt = new Date().toISOString()
      const envelope: DraftEnvelope<T> = { version: 1, savedAt, value }
      window.localStorage.setItem(storageKey, JSON.stringify(envelope))
      setSavedState({ key: storageKey, savedAt })
    } catch {
      // Private mode/storage quotas are recoverable: the form still works, just without draft persistence.
    }
  }, [dirty, enabled, storageKey, value])

  useEffect(() => {
    if (!enabled || !dirty) return
    const timer = window.setTimeout(saveNow, debounceMs)
    return () => window.clearTimeout(timer)
  }, [debounceMs, dirty, enabled, saveNow])

  useEffect(() => {
    if (!enabled || !dirty) return
    const warn = (event: BeforeUnloadEvent) => {
      saveNow()
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty, enabled, saveNow])

  const clearDraft = useCallback(() => {
    try { window.localStorage.removeItem(storageKey) } catch { /* ignore */ }
    setSavedState({ key: storageKey, savedAt: null })
  }, [storageKey])

  // If the dialog context changes before any new save, read that context's timestamp directly.
  // No state synchronization effect is needed, so identity/context switches cannot cascade renders.
  const lastSavedAt = savedState.key === storageKey ? savedState.savedAt : readSavedAt(storageKey)

  return { lastSavedAt, saveNow, clearDraft }
}
